'use client';

import Icon from '@/components/ui/AppIcon';

interface ServiceTermsSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function ServiceTermsSection({ isExpanded, onToggle }: ServiceTermsSectionProps) {
  const serviceTerms = [
    {
      title: 'Reservation Agreements',
      description: 'All reservations are subject to availability and confirmation. A binding contract is formed upon receipt of booking confirmation.',
      details: [
        'Booking confirmation sent via email within 24 hours',
        'Reservation valid only after payment confirmation',
        'Early check-in and late check-out subject to availability and additional charges'
      ]
    },
    {
      title: 'Property Usage Guidelines',
      description: 'Guests must use the property responsibly and in accordance with local laws and apartment regulations.',
      details: [
        'Maximum occupancy limits strictly enforced for safety',
        'Smoking prohibited in all indoor areas',
        'Pets allowed only in designated pet-friendly apartments with prior approval',
        'Noise restrictions apply between 10 PM and 8 AM'
      ]
    },
    {
      title: 'Payment Obligations',
      description: 'Full payment terms and conditions for your reservation at Proximus Serviced Apartments.',
      details: [
        'Advance payment of 30% required at time of booking',
        'Balance payment due 7 days before check-in',
        'Accepted payment methods: Credit/Debit cards, UPI, Bank Transfer',
        'Security deposit refunded within 7 business days after checkout',
        'Additional charges for extra services billed separately'
      ]
    },
    {
      title: 'Cancellation Procedures',
      description: 'Cancellation terms vary based on booking type and timing. Please refer to our detailed cancellation policy.',
      details: [
        'Free cancellation up to 14 days before check-in',
        '50% refund for cancellations 7-14 days before check-in',
        'No refund for cancellations within 7 days of check-in',
        'Modification requests subject to availability and may incur charges',
        'Force majeure exceptions apply - see Force Majeure section'
      ]
    }
  ];

  return (
    <section id="service-terms" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 1</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Service Terms
            </h2>
            <p className="text-lg text-text-secondary">
              Comprehensive terms covering reservation agreements, property usage, payment obligations, and cancellation procedures.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Detailed Service Terms</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand all service terms and conditions</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                {serviceTerms.map((term, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <h4 className="text-lg font-semibold text-text-primary mb-2">{term.title}</h4>
                    <p className="text-text-secondary mb-4">{term.description}</p>
                    <ul className="space-y-2">
                      {term.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}